import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminAPI } from '../api';

/**
 * Searchable CRA employee picker for admin inviter attribution (`invited_by_id`).
 *
 * @param {boolean} hideLabel — Set true when the parent already renders a section title
 *   (e.g. UserInviteForm) to avoid duplicate "CRA INTERNAL CONTACT" headings.
 */
const CraInternalContactSelect = ({
  className = '',
  hideLabel = false,
  label = 'CRA Internal Contact',
  value,
  selectedEmail,
  onChange,
  onInteraction,
  disabled = false,
  selectedSummaryLabel = 'CRA contact for this invite: ',
  emptySummaryText = 'No CRA internal contact selected — type at least two characters above to search',
}) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const seq = useRef(0);

  const selectedId = value != null && value !== '' ? Number(value) : null;

  const fetchOptions = useCallback(async (q) => {
    const trimmed = (q || '').trim();
    if (trimmed.length < 2) {
      setOptions([]);
      return;
    }
    const n = ++seq.current;
    setLoading(true);
    try {
      const data = await adminAPI.getCraInternalContacts(trimmed);
      if (n !== seq.current) return;
      setOptions(Array.isArray(data.users) ? data.users : []);
    } catch {
      if (n !== seq.current) return;
      setOptions([]);
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchOptions(query), 280);
    return () => clearTimeout(t);
  }, [query, fetchOptions]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const displayLabel = (opt) =>
    opt?.label && opt.label.trim() ? `${opt.label} <${opt.email}>` : opt?.email;

  return (
    <div className={className} ref={rootRef}>
      {!hideLabel && (
        <p className="text-sm uppercase text-charcoal font-bold">{label}</p>
      )}
      <div className={`relative ${hideLabel ? '' : 'mt-4'}`}>
        <input
          type="text"
          data-testid="cra-internal-contact-query"
          className="w-full p-2 border-2 border-gray rounded-lg bg-white"
          placeholder="Type at least 2 characters to search CRA contacts…"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && !disabled && (query.trim().length >= 2 || options.length > 0) && (
          <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border-2 border-gray bg-white shadow-lg">
            {loading && (
              <li className="px-3 py-2 text-sm text-gray-600">Searching…</li>
            )}
            {!loading &&
              options.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50"
                    onClick={() => {
                      onInteraction?.();
                      onChange(opt.id, opt.email);
                      setQuery('');
                      setOpen(false);
                      setOptions([]);
                    }}
                  >
                    {displayLabel(opt)}
                  </button>
                </li>
              ))}
            {!loading && options.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-600">No matches</li>
            )}
          </ul>
        )}
      </div>
      {selectedId ? (
        <p className="mt-2 text-sm text-charcoal" data-testid="cra-internal-contact-summary">
          <span className="font-semibold">{selectedSummaryLabel}</span>
          {selectedEmail || `user #${selectedId}`}
        </p>
      ) : (
        <p
          className="mt-2 text-sm text-gray-600 italic"
          data-testid="cra-internal-contact-summary-empty"
        >
          {emptySummaryText}
        </p>
      )}
    </div>
  );
};

export default CraInternalContactSelect;
